import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { Conversation, Message, Agent, AIHandoff } from '../types';
import { apiService } from '../services/api';
import { realtimeService } from '../services/supabase';
import { toast } from 'react-hot-toast';

interface ChatState {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: Message[];
  agents: Agent[];
  aiHandoffs: AIHandoff[];
  loading: boolean;
  error: string | null;
  currentUser: any;
}

type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CONVERSATIONS'; payload: Conversation[] }
  | { type: 'ADD_CONVERSATION'; payload: Conversation }
  | { type: 'SET_ACTIVE_CONVERSATION'; payload: Conversation | null }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'UPDATE_MESSAGE'; payload: Message }
  | { type: 'SET_AGENTS'; payload: Agent[] }
  | { type: 'UPDATE_AGENT'; payload: Agent }
  | { type: 'SET_AI_HANDOFFS'; payload: AIHandoff[] }
  | { type: 'ADD_AI_HANDOFF'; payload: AIHandoff }
  | { type: 'UPDATE_CONVERSATION'; payload: Conversation }
  | { type: 'SET_CURRENT_USER'; payload: any };

const initialState: ChatState = {
  conversations: [],
  activeConversation: null,
  messages: [],
  agents: [],
  aiHandoffs: [],
  loading: false,
  error: null,
  currentUser: null,
};

const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };
    case 'ADD_CONVERSATION':
      return { ...state, conversations: [...state.conversations, action.payload] };
    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversation: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map(msg =>
          msg.id === action.payload.id ? action.payload : msg
        ),
      };
    case 'SET_AGENTS':
      return { ...state, agents: action.payload };
    case 'UPDATE_AGENT':
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.id === action.payload.id ? action.payload : agent
        ),
      };
    case 'SET_AI_HANDOFFS':
      return { ...state, aiHandoffs: action.payload };
    case 'ADD_AI_HANDOFF':
      return { ...state, aiHandoffs: [...state.aiHandoffs, action.payload] };
    case 'UPDATE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.id === action.payload.id ? action.payload : conv
        ),
        activeConversation:
          state.activeConversation?.id === action.payload.id
            ? action.payload
            : state.activeConversation,
      };
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };
    default:
      return state;
  }
};

const ChatContext = createContext<{
  state: ChatState;
  loadConversations: (filters?: any) => Promise<void>;
  selectConversation: (id: string) => Promise<void>;
  sendMessage: (content: string, messageType?: string) => Promise<void>;
  assignAgent: (conversationId: string, agentId: string) => Promise<void>;
  takeoverFromAI: (conversationId: string, reason?: string) => Promise<void>;
  resumeAI: (conversationId: string) => Promise<void>;
  updateAgentStatus: (status: string) => Promise<void>;
} | null>(null);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  const loadInitialData = useCallback(async () => {
    // Don't show loading for initial data load to prevent UI flickering
    try {
      // Set a mock user so the app works without backend
      dispatch({ type: 'SET_CURRENT_USER', payload: { 
        id: 'demo-user', 
        name: 'Demo User', 
        role: 'agent',
        email: 'demo@example.com'
      }});

             // Set mock data so the app looks functional
       const mockConversations: Conversation[] = [
         {
           id: '1',
           customer_phone: '+1234567890',
           customer_name: 'John Doe',
           channel: 'whatsapp' as const,
           status: 'active' as const,
           assigned_agent: 'demo-user',
           created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
           updated_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 min ago
           last_message: {
             id: '1',
             conversation_id: '1',
             content: 'Hi, I need help with my order',
             timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
             sender_type: 'customer' as const,
             message_type: 'text' as const,
             is_from_ai: false
           },
           unread_count: 2
         },
         {
           id: '2',
           customer_phone: '+1234567891',
           customer_name: 'Jane Smith',
           channel: 'web' as const,
           status: 'pending' as const,
           assigned_agent: undefined,
           created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4 hours ago
           updated_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
           last_message: {
             id: '2',
             conversation_id: '2',
             content: 'Can someone help me with billing?',
             timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
             sender_type: 'customer' as const,
             message_type: 'text' as const,
             is_from_ai: false
           },
           unread_count: 1
         },
         {
           id: '3',
           customer_phone: '+1234567892',
           customer_name: 'Mike Wilson',
           channel: 'whatsapp' as const,
           status: 'ai-handled' as const,
           assigned_agent: undefined,
           created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6 hours ago
           updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
           last_message: {
             id: '3',
             conversation_id: '3',
             content: 'Thank you for the information!',
             timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
             sender_type: 'customer' as const,
             message_type: 'text' as const,
             is_from_ai: false
           },
           unread_count: 0
         }
       ];

       dispatch({ type: 'SET_CONVERSATIONS', payload: mockConversations });
       dispatch({ type: 'SET_AGENTS', payload: [] });
       dispatch({ type: 'SET_AI_HANDOFFS', payload: [] });

    } catch (error: any) {
      console.error('Failed to load initial data:', error);
    }
  }, []); // Remove all dependencies

  const setupRealtimeSubscriptions = useCallback(() => {
    // Temporarily disable realtime subscriptions to prevent errors
    try {
      // Subscribe to conversation changes
      realtimeService.subscribeToConversations((payload) => {
        if (payload.eventType === 'INSERT') {
          // Use ADD_CONVERSATION action to avoid state dependency
          dispatch({ type: 'ADD_CONVERSATION', payload: payload.new });
        } else if (payload.eventType === 'UPDATE') {
          dispatch({ type: 'UPDATE_CONVERSATION', payload: payload.new });
        }
      });

      // Subscribe to AI handoffs
      realtimeService.subscribeToAIHandoffs((payload) => {
        if (payload.eventType === 'INSERT') {
          dispatch({ type: 'ADD_AI_HANDOFF', payload: payload.new });
          toast('New AI handoff request', {
            icon: '🤖',
            duration: 5000,
          });
        }
      });
    } catch (error) {
      console.warn('Could not setup realtime subscriptions:', error);
    }
  }, []); // Remove state.conversations dependency

  useEffect(() => {
    // Only load data once when component mounts
    let mounted = true;
    
    const initializeData = async () => {
      if (!mounted) return;
      await loadInitialData();
      if (!mounted) return;
      setupRealtimeSubscriptions();
    };

    initializeData();

    return () => {
      mounted = false;
      realtimeService.unsubscribeAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove all dependencies to prevent re-running

  const loadConversations = async (filters?: any) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const conversations = await apiService.getConversations(filters);
      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });
    } catch (error: any) {
      console.warn('Failed to load conversations:', error);
      dispatch({ type: 'SET_CONVERSATIONS', payload: [] });
      // Only show error toast if user explicitly tries to load conversations
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const selectConversation = async (id: string) => {
    try {
      // For demo purposes, find conversation from mock data and add mock messages
      const conversation = state.conversations.find(c => c.id === id);
      if (!conversation) {
        toast.error('Conversation not found');
        return;
      }

      // Create mock messages for the selected conversation
      const mockMessages = [
        {
          id: `${id}_1`,
          conversation_id: id,
          content: conversation.last_message?.content || 'Hello, how can I help you?',
          timestamp: conversation.last_message?.timestamp || new Date().toISOString(),
          sender_type: 'customer' as const,
          message_type: 'text' as const,
          is_from_ai: false
        },
        {
          id: `${id}_2`,
          conversation_id: id,
          content: 'Thank you for contacting us. How can we assist you today?',
          timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minutes ago
          sender_type: 'agent' as const,
          message_type: 'text' as const,
          is_from_ai: false
        }
      ];

      dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversation });
      dispatch({ type: 'SET_MESSAGES', payload: mockMessages });

      // Subscribe to messages for this conversation (disabled to prevent errors)
      try {
        realtimeService.subscribeToMessages(id, (payload) => {
          if (payload.eventType === 'INSERT') {
            dispatch({ type: 'ADD_MESSAGE', payload: payload.new });
          } else if (payload.eventType === 'UPDATE') {
            dispatch({ type: 'UPDATE_MESSAGE', payload: payload.new });
          }
        });
      } catch (error) {
        console.warn('Could not subscribe to messages:', error);
      }
    } catch (error: any) {
      console.warn('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
    }
  };

  const sendMessage = async (content: string, messageType: string = 'text') => {
    if (!state.activeConversation) return;

    try {
      await apiService.sendMessage(state.activeConversation.id, {
        content,
        message_type: messageType as any,
        sender_type: 'agent',
      });
      // Message will be added via realtime subscription
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      toast.error('Failed to send message');
    }
  };

  const assignAgent = async (conversationId: string, agentId: string) => {
    try {
      const conversation = await apiService.assignAgent(conversationId, agentId);
      dispatch({ type: 'UPDATE_CONVERSATION', payload: conversation });
      toast.success('Agent assigned successfully');
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      toast.error('Failed to assign agent');
    }
  };

  const takeoverFromAI = async (conversationId: string, reason?: string) => {
    if (!state.currentUser) return;

    try {
      await apiService.takeoverFromAI(conversationId, state.currentUser.id, reason);
      toast.success('Successfully took over from AI');
      // Refresh conversation data
      await selectConversation(conversationId);
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      toast.error('Failed to takeover from AI');
    }
  };

  const resumeAI = async (conversationId: string) => {
    try {
      await apiService.resumeAI(conversationId);
      toast.success('AI resumed successfully');
      // Refresh conversation data
      await selectConversation(conversationId);
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      toast.error('Failed to resume AI');
    }
  };

  const updateAgentStatus = async (status: string) => {
    if (!state.currentUser) return;

    try {
      const agent = await apiService.updateAgentStatus(state.currentUser.id, status);
      dispatch({ type: 'UPDATE_AGENT', payload: agent });
      toast.success(`Status updated to ${status}`);
    } catch (error: any) {
      console.warn('Failed to update agent status:', error);
      // Don't show error toast for status updates to prevent spam
    }
  };

  return (
    <ChatContext.Provider
      value={{
        state,
        loadConversations,
        selectConversation,
        sendMessage,
        assignAgent,
        takeoverFromAI,
        resumeAI,
        updateAgentStatus,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};
